import { BaseModelPrivatization } from "./BaseModelPrivatization";
import { StaticDataPrivatizationType } from "./enum/StaticDataPrivatizationType";
import { Injectable } from "@angular/core";
import { SystemConfigPromiseService } from "shared/auth-services";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

@Injectable()
export class AccountModelPrivatization extends BaseModelPrivatization<
  string,
  string,
  CubiqTS.AccountImpl
> {
  getBranchPrivateLabel(branchCd: string): string {
    return `PRIVATE - ${branchCd}`;
  }
  getPrivateLabel(): string {
    return this.privateMask;
  }
  getRestrictedLabel(): string {
    return "Restricted";
  }
  getPrefixPrivate(): string {
    return this.prefix + "_private";
  }
  getPrefixRestricted(): string {
    return this.prefix + "_restricted";
  }

  private privateMask = "-PRIVATE-";
  private prefix = "__bfx_privatization";

  private readyResolve;
  private readyReject;

  public readonly ready = new Promise<void>((res, rej) => {
    this.readyResolve = res;
    this.readyReject = rej;
  });

  public constructor(private sysConfigService: SystemConfigPromiseService) {
    super();

    Promise.all([this.sysConfigService.isPrimed()]).then(() => {
      const systemPrivateMask = this.sysConfigService.hardGetSystemConfig("PRIVATE_MASK");

      if (typeof systemPrivateMask === "string" && systemPrivateMask.length > 0) {
        this.privateMask = this.sysConfigService.hardGetSystemConfig("PRIVATE_MASK");
      }

      this.readyResolve();
    });
  }

  public asModelPrivatized(label: string, key: string, id: string): CubiqTS.AccountImpl {
    let account = new CubiqTS.AccountImpl();

    account.id = label;
    account.name = label;

    // account.clientId // don't set invalid
    account.extendedInfo.set("__bfx_privatization_key", key);

    return account;
  }

  public asModelRestrictedClientSegment(
    clientSegment: string,
    restrictedKey: string,
    restrictedValue: CubiqTS.AccountImpl,
  ): { key: string; value: CubiqTS.AccountImpl } {
    let restrictedClientSegment = super.asModelRestrictedClientSegment(
      clientSegment,
      restrictedKey,
      restrictedValue,
    );

    if (restrictedClientSegment == null) {
      return null;
    }

    restrictedClientSegment.key = `${restrictedKey}_${clientSegment}`;
    restrictedClientSegment.value.clientId = `${restrictedClientSegment.value.clientId}_${clientSegment}`;
    restrictedClientSegment.value.id = clientSegment;
    restrictedClientSegment.value.name = clientSegment;
    restrictedClientSegment.value.extendedInfo.set(
      "__bfx_privatization_key",
      restrictedClientSegment.key,
    );

    return restrictedClientSegment;
  }

  public asModelRestrictedIdName(
    id: string,
    name: string,
    restrictedKey: string,
    restrictedValue: CubiqTS.AccountImpl,
  ): { key: string; value: CubiqTS.AccountImpl } {
    let restrictedIdName = super.asModelRestrictedIdName(id, name, restrictedKey, restrictedValue);

    if (restrictedIdName == null) {
      return null;
    }

    restrictedIdName.key = `id:${id}_name:${name}`;
    restrictedIdName.value.clientId = `${restrictedIdName.value.clientId}_id:${id}_name:${name}`;
    restrictedIdName.value.id = id;
    restrictedIdName.value.name = name;
    restrictedIdName.value.extendedInfo.set("__bfx_privatization_key", restrictedIdName.key);

    return restrictedIdName;
  }

  public getBranchPrivate(branchCd: string): Map<string, CubiqTS.AccountImpl> {
    let accounts = new Map<string, CubiqTS.AccountImpl>();
    let key = `${this.prefix}_private_${branchCd}`;
    let account = this.asModelPrivatized(this.getBranchPrivateLabel(branchCd), key, key);
    StaticDataPrivatizationType.setPrivatization(
      account,
      StaticDataPrivatizationType.BranchPrivate,
    );
    accounts.set(`${this.prefix}_private_${branchCd}`, account);

    return accounts;
  }

  public getBranchPrivateKey(branchCd: string, account?: CubiqTS.AccountImpl | null): string {
    return this.getBranchPrivateId(branchCd, account);
  }

  public getBranchPrivateId(branchCd: string, account?: CubiqTS.AccountImpl | null): string {
    return `${this.prefix}_private_${branchCd}`;
  }

  public getPrivate(): Map<string, CubiqTS.AccountImpl> {
    let accounts = new Map<string, CubiqTS.AccountImpl>();
    let key = `${this.prefix}_private`;
    let account = this.asModelPrivatized(this.getPrivateLabel(), key, key);
    StaticDataPrivatizationType.setPrivatization(account, StaticDataPrivatizationType.Private);
    accounts.set(`${this.prefix}_private`, account);

    return accounts;
  }

  public getPrivateKey(account?: CubiqTS.AccountImpl | null): string {
    return this.getPrivateId(account);
  }

  public getPrivateId(account?: CubiqTS.AccountImpl | null): string {
    return `${this.prefix}_private`;
  }

  public getRestricted(): Map<string, CubiqTS.AccountImpl> {
    let accounts = new Map<string, CubiqTS.AccountImpl>();
    let key = `${this.prefix}_private`;
    let account = this.asModelPrivatized(this.getRestrictedLabel(), key, key);
    StaticDataPrivatizationType.setPrivatization(account, StaticDataPrivatizationType.Restricted);
    accounts.set(`${this.prefix}_restricted`, account);

    return accounts;
  }

  public getRestrictedKey(account?: CubiqTS.AccountImpl | null): string {
    return this.getRestrictedId(account);
  }

  public getRestrictedId(account?: CubiqTS.AccountImpl | null): string {
    return `${this.prefix}_restricted`;
  }
}
